/**
 * Task Lifecycle Handlers — TASK_CREATED, TASK_RESUMED, resume:failed,
 * TASK_UPDATE, TASK_DESTROY.
 */

import type { Socket } from "socket.io-client";
import { EVENTS } from "@/constants/events";
import { useTaskStore, buildNewTask, vouchForOwnTask } from "../taskStore";
import { useBrowserStore } from "../browserStore";
import { useHistoryStore } from "../historyStore";
import { useFileUrlStore } from "../fileUrlStore";
import { DEFAULT_AGENT, serverDetailsToPendingInteraction } from "../types";
import type { TaskOutput } from "../types";
import { enqueueMainPending } from "../pendingQueue";
import { lifecycleToBucket, markTaskDestroyed } from "./taskStreamHandlers";
import { schedulePersist } from "../persistence";
import { toCanonicalPath } from "@/lib/file-paths";
import type { ClientPayloadOf } from "../socketService";

export function registerTaskLifecycleHandlers(socket: Socket): () => void {
  const taskStore = useTaskStore;
  const historyStore = useHistoryStore;

  // NOT PayloadOf: the task:* schemas embed the DEAD shared Task shape (agentId, no
  // lifecycleState / pendingDetails, Date fields) that nothing sends — the schema
  // contradicts the wire (s2-07b finding, see PR s2-07c). Inline until fixed.
  const handleTaskCreated = (data: {
    taskId?: string;
    task: { id: string; userId: string; title?: string; agentSlug?: string };
    // LEGACY — only present when server ran the legacy path for an old client
    // that omitted taskId. New clients receive only taskId/task. Phase 3 removal.
    correlationId?: string;
  }) => {
    const state = taskStore.getState();
    const newTaskId = data.taskId || data.task.id;

    // Originating tab: task is already in the store under the same UUID. The
    // server sends this to the owner only, after the row exists: proof that
    // lifts a sync which outran the insert (vouchForOwnTask).
    if (state.tasks.has(newTaskId)) {
      vouchForOwnTask(newTaskId);
      return;
    }

    // Other tab (multi-tab sync): add the task to this tab's store so the
    // user sees it appear in the sidebar and can select it.
    taskStore.setState((s) => {
      s.tasks.set(
        newTaskId,
        buildNewTask(newTaskId, data.task.agentSlug || DEFAULT_AGENT, {
          phase: "idle",
          title: data.task.title || "New Task",
        })
      );
    });
    schedulePersist(taskStore.getState().tasks);
  };

  // NOT PayloadOf: same dead-Task schema as TASK_CREATED above.
  const handleTaskResumed = (data: {
    task: {
      id: string;
      userId: string;
      title?: string;
      agentSlug?: string;
      lifecycleState?: string;
      pendingDetails?: { reason: string; [key: string]: unknown };
    };
  }) => {
    const taskId = data.task.id;
    const agentSlug = data.task.agentSlug || DEFAULT_AGENT;
    const state = taskStore.getState();
    state._setSelectedTaskId(taskId);

    const details = data.task.pendingDetails;
    const isAwaiting = data.task.lifecycleState === "awaiting";
    const pendingInteraction =
      isAwaiting && details
        ? serverDetailsToPendingInteraction(taskId, agentSlug, details)
        : null;

    if (!state.tasks.has(taskId)) {
      // Canonicalise filePath on every incoming output so downstream lookup sites
      // (chip click, FileOutputCard) can compare with === instead of running
      // ad-hoc normalisation. See apps/web/src/lib/file-paths.ts. This is the
      // third TaskOutput ingest boundary, alongside syncHandlers (MESSAGE_SYNC)
      // and contextPanelHandlers (TASK_OUTPUT_REGISTERED) — they must all
      // canonicalise or the lookup contract leaks.
      const rawOutputs: TaskOutput[] = (data.task as any).outputs || [];
      const canonicalOutputs: TaskOutput[] = rawOutputs.map((o) => ({
        ...o,
        filePath: toCanonicalPath(o.filePath ?? `files/${o.title}`),
      }));

      state._createTask(taskId, agentSlug, {
        title: data.task.title || "Resumed Task",
        pendingInteraction,
        outputs: canonicalOutputs,
        todos: (data.task as any).todos || [],
      });

      // Pre-populate fileUrlStore so FileOutputCard / chip clicks can resolve
      // CDN URLs without an extra round-trip. Mirrors the syncHandlers
      // MESSAGE_SYNC path — same lookup contract.
      const store = useFileUrlStore.getState();
      for (const o of canonicalOutputs) {
        // `TaskOutput.filePath` is typed optional even though we just
        // canonicalised it above — the loop has to narrow explicitly so
        // store.get / store.set get a `string`, not `string | undefined`.
        const filePath = o.filePath;
        if (filePath && o.url && o.title) {
          if (!store.get(filePath)) {
            store.set(filePath, { url: o.url, mediaType: o.mediaType || "" });
          }
        }
      }
    } else {
      if (true) {
        state._mutateTasks((tasks) => {
          const task = tasks.get(taskId);
          if (!task) return;
          task.phase = "idle";
          // Server hydrate joins any live main-lane asks instead of replacing
          // them (a replace dropped a live sibling; enqueue dedups by id).
          if (pendingInteraction) enqueueMainPending(task, pendingInteraction);
        });
      }
    }

    socket.emit(EVENTS.MESSAGE_SYNC_REQUEST, { taskId });
  };

  const handleResumeFailed = (
    data: ClientPayloadOf<typeof EVENTS.TASK_RESUME_FAILED>
  ) => {
    taskStore.setState((s) => {
      s.selectedTaskId = null;
      s.resumeError = data.error || "Failed to resume task";
    });
    // Cross-section navigation: use window.location.assign (full reload) so
    // Next.js mounts the /home layout cleanly. history.replaceState would
    // rewrite the URL bar without mounting /home, leaving the user on a
    // blank task page. This is a rare error path, so the full reload cost
    // is acceptable. (Don't use router.push — socket handlers are outside
    // the React tree and can't access useRouter.)
    if (
      typeof window !== "undefined" &&
      window.location.pathname.includes(data.taskId)
    ) {
      window.location.assign("/home");
    }
  };

  // NOT PayloadOf: TaskUpdatePayloadSchema is { task } while the wire is { taskId, title,
  // status } — the schema contradicts the wire (s2-07b finding). Inline until fixed.
  const handleTaskUpdate = (data: {
    taskId: string;
    title?: string;
    status?: string;
  }) => {
    if (!data.taskId) return;
    taskStore.getState()._mutateTasks((tasks) => {
      const task = tasks.get(data.taskId);
      if (!task) return;
      if (data.title) task.title = data.title;
    });
  };

  // TASK_SYNC_HINT — the server healed a relay flap and accepted a late (buffered)
  // turn terminal AFTER reconcile already idled the task and pushed this tab a
  // synthetic interrupted AGENT_END. This tab's own socket never blipped, so it
  // still shows the interrupted footer and the recovered output lives only in the
  // DB (persisted server-side by MESSAGE_COMPLETE). Re-pull so the completed turn
  // renders live. Guard on knowing the task — a hint for a task this tab never
  // opened (or a stale one) is a no-op. Mirrors handleTaskResumed's re-pull.
  const handleTaskSyncHint = (
    data: ClientPayloadOf<typeof EVENTS.TASK_SYNC_HINT>
  ) => {
    const taskId = data?.taskId;
    if (!taskId) return;
    if (!taskStore.getState().tasks.has(taskId)) return;
    socket.emit(EVENTS.MESSAGE_SYNC_REQUEST, { taskId });
  };

  const handleTaskDestroy = (
    data: ClientPayloadOf<typeof EVENTS.TASK_DESTROY>
  ) => {
    if (!data.taskId) return;
    markTaskDestroyed(data.taskId);

    const state = taskStore.getState();
    state._mutateTasks((tasks) => {
      tasks.delete(data.taskId);
    });
    if (state.selectedTaskId === data.taskId) {
      state._setSelectedTaskId(null);
    }

    // Clean up any panel-open slot held by this task. Server-driven path:
    // the originating tab (or whatever surface deleted the task) already
    // dealt with the browser session, so we do NOT emit BROWSER_CLOSE_SESSION
    // here. Local-state cleanup only — drops the phantom that would
    // otherwise keep `wasLast=false` on a future close and freeze the
    // 'browsing' display overlay.
    useBrowserStore.getState().cleanupForDeletedTask(data.taskId);

    const hs = historyStore.getState();
    hs._setTaskHistory((prev) => {
      const task = prev.find((t) => t.id === data.taskId);
      if (!task) return prev;
      const bucket = lifecycleToBucket(task.lifecycleState || "idle");
      hs._setTaskStats((stats) => {
        if (!stats) return stats;
        const n = {
          ...stats,
          total: Math.max(0, stats.total - 1),
          byStatus: { ...stats.byStatus },
          byLifecycle: { ...stats.byLifecycle },
        };
        n.byLifecycle[bucket] = Math.max(0, n.byLifecycle[bucket] - 1);
        if (task.status === "active")
          n.byStatus.active = Math.max(0, n.byStatus.active - 1);
        else if (task.status === "paused")
          n.byStatus.paused = Math.max(0, n.byStatus.paused - 1);
        return n;
      });
      return prev.filter((t) => t.id !== data.taskId);
    });
  };

  socket.on(EVENTS.TASK_CREATED, handleTaskCreated);
  socket.on(EVENTS.TASK_RESUMED, handleTaskResumed);
  socket.on(EVENTS.TASK_RESUME_FAILED, handleResumeFailed);
  socket.on(EVENTS.TASK_UPDATE, handleTaskUpdate);
  socket.on(EVENTS.TASK_SYNC_HINT, handleTaskSyncHint);
  socket.on(EVENTS.TASK_DESTROY, handleTaskDestroy);

  return () => {
    socket.off(EVENTS.TASK_CREATED, handleTaskCreated);
    socket.off(EVENTS.TASK_RESUMED, handleTaskResumed);
    socket.off(EVENTS.TASK_RESUME_FAILED, handleResumeFailed);
    socket.off(EVENTS.TASK_UPDATE, handleTaskUpdate);
    socket.off(EVENTS.TASK_SYNC_HINT, handleTaskSyncHint);
    socket.off(EVENTS.TASK_DESTROY, handleTaskDestroy);
  };
}
